<template>
  <div class="min-h-screen bg-gray-900 flex flex-col">
    <!-- Header -->
    <header class="bg-gray-800 text-white p-4 shadow-lg">
      <div class="container mx-auto">
        <h1 class="text-2xl font-bold">Video Player</h1>
        <p class="text-gray-300 text-sm mt-1">Universal Video Player with Enhanced Features</p>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 container mx-auto ">
      <div class="max-w-7xl mx-auto">
        <!-- Player Container -->
        <div class="bg-black rounded-lg overflow-hidden shadow-2xl mb-6">
          <!-- Debug Info -->
          <div class="p-4 bg-blue-900 text-white text-sm mb-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p><strong>Debug:</strong> Component Loading Status</p>
                <p>Video Source: {{ videoConfig.src }}</p>
                <p>Source Type: {{ getSourceType(videoConfig.src) }}</p>
                <p>Client-side: {{ isClient }}</p>
                <p>Current Time: {{ formatTime(currentTime) }} / {{ formatTime(duration) }}</p>
              </div>
              <div>
                <p><strong>Audio/Subtitle Status:</strong></p>
                <p>📝 Subtitle: {{ subtitleConfig.enabled ? `${getCurrentSubtitleLabel()} (${subtitleConfig.currentTrack})` : 'Off' }}</p>
                <p>🎵 Soundtrack: {{ getCurrentSoundtrackLabel() }} ({{ soundtrackConfig.currentTrack }})</p>
                <p>🔊 Tracks: {{ subtitleConfig.tracks.length }} subtitles, {{ soundtrackConfig.tracks.length }} audio</p>
              </div>
            </div>
          </div>
          
          <!-- Video Player (Client-side only) -->
          <ClientOnly fallback="Loading video player...">
            <UniversalPlayer
              ref="playerRef"
              v-bind="playerConfig"
              :auto-hide-controls="controlsConfig.autoHide"
              :always-show-controls="controlsConfig.alwaysShow"
              :controls-hide-delay="controlsConfig.hideDelay"
              :show-controls-on-hover="controlsConfig.showOnHover"
              :show-controls-on-touch="controlsConfig.showOnTouch"
              @ready="onPlayerReady"
              @play="onPlayerPlay"
              @pause="onPlayerPause"
              @time-update="onTimeUpdate"
              @error="onPlayerError"
              @quality-changed="onQualityChanged"
              @enter-pip="onEnterPip"
              @leave-pip="onLeavePip"
              @subtitle-track-changed="onSubtitleTrackChanged"
              @soundtrack-changed="onSoundtrackChanged"
              @subtitle-error="onSubtitleError"
              @soundtrack-error="onSoundtrackError"
            />
            <template #fallback>
              <div class="flex items-center justify-center h-64 bg-gray-800">
                <div class="text-white text-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                  <p>Loading Video Player...</p>
                </div>
              </div>
            </template>
          </ClientOnly>
        </div>

        <!-- Player Controls Panel -->
        <div class="bg-gray-800 rounded-lg p-6 text-white shadow-lg">
          <h3 class="text-lg font-semibold mb-4">Player Controls</h3>
          
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <!-- Basic Controls -->
            <div class="space-y-3">
              <h4 class="font-medium text-gray-300 border-b border-gray-600 pb-2">Basic Controls</h4>
              <button 
                @click="togglePlay" 
                class="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 font-medium"
              >
                {{ isPlaying ? 'Pause' : 'Play' }}
              </button>
              <button 
                @click="stopVideo" 
                class="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 font-medium"
              >
                Stop
              </button>
              <button 
                @click="skipBack" 
                class="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors duration-200 font-medium"
              >
                Skip -10s
              </button>
              <button 
                @click="skipForward" 
                class="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors duration-200 font-medium"
              >
                Skip +10s
              </button>
            </div>

            <!-- Quick Source Switcher -->
            <div class="space-y-3">
              <h4 class="font-medium text-gray-300 border-b border-gray-600 pb-2">Quick Source</h4>
              <button 
                @click="switchToM3u8" 
                class="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors duration-200 font-medium"
              >
                📺 M3U8 Stream
              </button>
              <button 
                @click="switchToYoutubeMp4" 
                class="w-full px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 font-medium"
              >
                🎬 YouTube
              </button>
              <button 
                @click="switchToMp4" 
                class="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors duration-200 font-medium"
              >
                🎥 MP4 Video
              </button>
            </div>

            <!-- Video Sources -->
            <div class="space-y-3">
              <h4 class="font-medium text-gray-300 border-b border-gray-600 pb-2">All Video Sources</h4>
              <button 
                v-for="source in videoSources" 
                :key="source.name"
                @click="changeVideoSource(source)"
                :disabled="source.disabled"
                :class="[
                  'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium text-left',
                  source.disabled 
                    ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                    : videoConfig.src === source.url 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-gray-600 hover:bg-gray-700'
                ]"
              >
                {{ source.name }}
              </button>
            </div>

            <!-- Advanced Features -->
            <div class="space-y-3">
              <h4 class="font-medium text-gray-300 border-b border-gray-600 pb-2">Advanced Features</h4>
              <button 
                @click="toggleWatermark" 
                class="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors duration-200 font-medium"
              >
                {{ watermarkConfig.show ? 'ปิด' : 'เปิด' }} Watermark
              </button>
              <button 
                @click="toggleSeeker" 
                :class="[
                  'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                  securityConfig.disableSeeker ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                ]"
              >
                {{ securityConfig.disableSeeker ? 'เปิด' : 'ปิด' }} Seeker Bar
              </button>
              <button 
                @click="toggleFullscreen" 
                class="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors duration-200 font-medium"
              >
                Toggle Fullscreen
              </button>
              <button 
                @click="togglePictureInPicture" 
                class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors duration-200 font-medium"
              >
                Picture-in-Picture
              </button>
              <button 
                @click="togglePreviewMode" 
                :class="[
                  'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                  previewConfig.enabled 
                    ? 'bg-yellow-600 hover:bg-yellow-700' 
                    : 'bg-gray-600 hover:bg-gray-700'
                ]"
              >
                {{ previewConfig.enabled ? 'ปิด' : 'เปิด' }} Preview Mode
              </button>
              <button 
                @click="toggleLogo" 
                :class="[
                  'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                  logoConfig.show 
                    ? 'bg-indigo-600 hover:bg-indigo-700' 
                    : 'bg-gray-600 hover:bg-gray-700'
                ]"
              >
                {{ logoConfig.show ? 'ซ่อน' : 'แสดง' }} Logo
              </button>
            </div>

            <!-- Control Bar Settings -->
            <div class="space-y-3">
              <h4 class="font-medium text-gray-300 border-b border-gray-600 pb-2">Control Bar Settings</h4>
              <button 
                @click="toggleAutoHide" 
                :class="[
                  'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                  controlsConfig.autoHide ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-600 hover:bg-gray-700'
                ]"
              >
                {{ controlsConfig.autoHide ? 'ปิด' : 'เปิด' }} Auto Hide
              </button>
              <button 
                @click="toggleAlwaysShow" 
                :class="[
                  'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                  controlsConfig.alwaysShow ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                ]"
              >
                {{ controlsConfig.alwaysShow ? 'ปิด' : 'เปิด' }} Always Show
              </button>
              <button 
                @click="cycleHideDelay" 
                class="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 font-medium"
              >
                Hide Delay: {{ controlsConfig.hideDelay / 1000 }}s
              </button>
              <button 
                @click="toggleHoverShow" 
                :class="[
                  'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                  controlsConfig.showOnHover ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-600 hover:bg-gray-700'
                ]"
              >
                {{ controlsConfig.showOnHover ? 'ปิด' : 'เปิด' }} Show on Hover
              </button>
            </div>

            <!-- Subtitle Controls -->
            <div class="space-y-3">
              <h4 class="font-medium text-green-300 border-b border-green-600 pb-2">Subtitle Settings</h4>
              <div class="space-y-3">
                <button 
                  @click="toggleSubtitles" 
                  :class="[
                    'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                    subtitleConfig.enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                  ]"
                >
                  {{ subtitleConfig.enabled ? 'ปิดคำบรรยาย' : 'เปิดคำบรรยาย' }}
                </button>
                <select 
                  v-model="subtitleConfig.currentTrack" 
                  @change="onSubtitleChange"
                  :disabled="!subtitleConfig.enabled"
                  class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  <option v-for="track in subtitleConfig.tracks" :key="track.id" :value="track.id">
                    {{ track.label }}
                  </option>
                </select>
                <div class="text-xs text-gray-400">
                  Current: {{ getCurrentSubtitleLabel() }}
                </div>
              </div>
            </div>

            <!-- Soundtrack Controls -->
            <div class="space-y-3">
              <h4 class="font-medium text-orange-300 border-b border-orange-600 pb-2">Soundtrack Settings</h4>
              <div class="space-y-3">
                <button 
                  @click="toggleSoundtrack" 
                  :class="[
                    'w-full px-4 py-2 rounded-lg transition-colors duration-200 font-medium',
                    soundtrackConfig.enabled ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-600 hover:bg-gray-700'
                  ]"
                >
                  {{ soundtrackConfig.enabled ? 'ปิดเสียงประกอบ' : 'เปิดเสียงประกอบ' }}
                </button>
                <select 
                  v-model="soundtrackConfig.currentTrack" 
                  @change="onSoundtrackChange"
                  :disabled="!soundtrackConfig.enabled"
                  class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                >
                  <option v-for="track in soundtrackConfig.tracks" :key="track.id" :value="track.id">
                    {{ track.label }}
                  </option>
                </select>
                <div class="text-xs text-gray-400">
                  Current: {{ getCurrentSoundtrackLabel() }}
                </div>
              </div>
            </div>

            <!-- Preview Mode Settings (แสดงเฉพาะเมื่อ preview mode เปิดอยู่) -->
            <div v-if="previewConfig.enabled" class="space-y-3">
              <h4 class="font-medium text-yellow-300 border-b border-yellow-600 pb-2">Preview Mode Settings</h4>
              <div class="space-y-3">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เริ่มต้นที่ (MM:SS หรือ วินาที)</label>
                  <input 
                    v-model="previewConfig.startTimeInput" 
                    type="text" 
                    placeholder="00:30 หรือ 30"
                    class="w-full px-3 py-2 bg-gray-600 text-white rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">สิ้นสุดที่ (MM:SS หรือ วินาที)</label>
                  <input 
                    v-model="previewConfig.endTimeInput" 
                    type="text" 
                    placeholder="01:30 หรือ 90"
                    class="w-full px-3 py-2 bg-gray-600 text-white rounded-lg focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div class="flex items-center space-x-2">
                  <input 
                    v-model="previewConfig.loop" 
                    type="checkbox" 
                    class="rounded text-yellow-500 focus:ring-yellow-500"
                  />
                  <label class="text-sm text-gray-300">เล่นซ้ำเมื่อสิ้นสุด</label>
                </div>
              </div>
            </div>
          </div>

          <!-- Configuration Presets (แสดงประโยชน์ของการจัดกลุ่ม) -->
          <div class="mt-6 p-4 bg-gray-700 rounded-lg">
            <h4 class="font-medium mb-3">Quick Configuration Presets</h4>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              <button 
                @click="presetConfigurations.enableAllFeatures()" 
                class="px-3 py-2 bg-green-600 hover:bg-green-700 rounded text-sm transition-colors duration-200"
              >
                🎛️ Full Features
              </button>
              <button 
                @click="presetConfigurations.minimalPlayer()" 
                class="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors duration-200"
              >
                🎵 Minimal
              </button>
              <button 
                @click="presetConfigurations.securityMode()" 
                class="px-3 py-2 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors duration-200"
              >
                🔒 Security
              </button>
              <button 
                @click="presetConfigurations.multiLanguageMode()" 
                class="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm transition-colors duration-200"
              >
                📝 Multi-Lang
              </button>
              <button 
                @click="presetConfigurations.audioOnlyMode()" 
                class="px-3 py-2 bg-orange-600 hover:bg-orange-700 rounded text-sm transition-colors duration-200"
              >
                🎵 Audio Focus
              </button>
            </div>
            <p class="text-xs text-gray-400 mt-2">
              การจัดกลุ่ม configuration ทำให้สามารถปรับค่าหลายๆ อย่างพร้อมกันได้ง่าย
            </p>
          </div>
        
          <!-- Configuration Status -->
          <div class="mt-6 p-4 bg-gray-700 rounded-lg">
            <h4 class="font-medium mb-3">Configuration Status</h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="text-sm space-y-1">
                <div class="flex justify-between">
                  <span>Video Config:</span>
                  <span class="text-green-400">✓ {{ Object.keys(videoConfig).length }} props</span>
                </div>
                <div class="flex justify-between">
                  <span>Player Controls:</span>
                  <span class="text-blue-400">⚙️ {{ Object.keys(playerControls).length }} controls</span>
                </div>
                <div class="flex justify-between">
                  <span>Watermark:</span>
                  <span class="text-orange-400">🔒 {{ watermarkConfig.show ? 'Active' : 'Disabled' }}</span>
                </div>
                <div class="flex justify-between">
                  <span>Seeker Bar:</span>
                  <span :class="securityConfig.disableSeeker ? 'text-red-400' : 'text-green-400'">
                    {{ securityConfig.disableSeeker ? '🚫 Disabled' : '✓ Enabled' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Logo:</span>
                  <span :class="logoConfig.show ? 'text-blue-400' : 'text-gray-400'">
                    {{ logoConfig.show ? '🏢 Visible' : '⚪ Hidden' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Subtitle:</span>
                  <span :class="subtitleConfig.enabled ? 'text-green-400' : 'text-gray-400'">
                    {{ subtitleConfig.enabled ? '📝 ' + getCurrentSubtitleLabel() : '⚪ Disabled' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Soundtrack:</span>
                  <span :class="soundtrackConfig.enabled ? 'text-orange-400' : 'text-gray-400'">
                    {{ soundtrackConfig.enabled ? '🎵 ' + getCurrentSoundtrackLabel() : '⚪ Original' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Preview Mode:</span>
                  <span :class="previewConfig.enabled ? 'text-yellow-400' : 'text-gray-400'">
                    {{ previewConfig.enabled ? `📺 Active (${previewConfig.startTime}s-${previewConfig.endTime}s)` : '⚪ Disabled' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Subtitles:</span>
                  <span :class="subtitleConfig.enabled ? 'text-green-400' : 'text-gray-400'">
                    {{ subtitleConfig.enabled ? `💬 ${getCurrentSubtitleLabel()}` : '⚪ Off' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Soundtrack:</span>
                  <span :class="soundtrackConfig.enabled && soundtrackConfig.currentTrack !== 'original' ? 'text-orange-400' : 'text-gray-400'">
                    {{ soundtrackConfig.enabled && soundtrackConfig.currentTrack !== 'original' ? `🎵 ${getCurrentSoundtrackLabel()}` : '🎤 Original' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Auto Hide:</span>
                  <span :class="controlsConfig.autoHide ? 'text-orange-400' : 'text-gray-400'">
                    {{ controlsConfig.autoHide ? `⏱️ ${controlsConfig.hideDelay/1000}s` : '⚪ Disabled' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span>Always Show:</span>
                  <span :class="controlsConfig.alwaysShow ? 'text-green-400' : 'text-gray-400'">
                    {{ controlsConfig.alwaysShow ? '👁️ Active' : '⚪ Disabled' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Player Info -->
          <div class="mt-6 p-4 bg-gray-700 rounded-lg">
            <h4 class="font-medium mb-2">Player Status</h4>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <span class="text-gray-400">Status:</span>
                <span class="ml-2 font-medium">{{ getPlayerStatus() }}</span>
              </div>
              <div>
                <span class="text-gray-400">Time:</span>
                <span class="ml-2 font-mono">{{ formatTime(getPlayerCurrentTime()) }} / {{ formatTime(getPlayerDuration()) }}</span>
              </div>
              <div>
                <span class="text-gray-400">Progress:</span>
                <span class="ml-2 font-medium">{{ getPlayerProgress() }}%</span>
              </div>
              <div>
                <span class="text-gray-400">Quality:</span>
                <span class="ml-2 font-medium">{{ getPlayerQuality() || 'Auto' }}</span>
              </div>
            </div>
            <!-- Session Info -->
            <div class="border-t border-gray-600 pt-3">
              <h5 class="font-medium mb-2 text-yellow-400">🎫 Session Information</h5>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="text-gray-400">Session ID:</span>
                  <span class="ml-2 font-mono text-xs">{{ getSessionId() || 'Not initialized' }}</span>
                </div>
                <div>
                  <span class="text-gray-400">Session Status:</span>
                  <span class="ml-2 font-medium text-green-400">{{ getSessionId() ? 'Active' : 'Inactive' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div> <!-- End Player Controls Panel -->
      </div> <!-- End max-w-4xl mx-auto -->
    </main>

    <!-- Footer -->
    <footer class="bg-gray-800 text-gray-300 p-4 text-center border-t border-gray-700">
      <p class="text-sm">Universal Video Player - Built with Vue 3 & Nuxt</p>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, defineAsyncComponent, nextTick, watch } from 'vue'
import { contentDeliveryClient, createStreamSource, getMultiQualityStreams } from '~/utils/streamUtils'

// Nuxt 3 client-side detection
const isClient = ref(false)

// Use defineAsyncComponent for better Nuxt 3 compatibility
const UniversalPlayer = defineAsyncComponent({
  loader: () => import('~/components/videoplayer/UniversalPlayer.vue'),
  loadingComponent: {
    template: `
      <div class="flex items-center justify-center h-64 bg-gray-800">
        <div class="text-white text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading Universal Player...</p>
        </div>
      </div>
    `
  },
  errorComponent: {
    template: `
      <div class="flex items-center justify-center h-64 bg-red-900">
        <div class="text-white text-center">
          <p class="mb-2">⚠️ Failed to load Universal Player</p>
          <p class="text-sm text-red-300">Using fallback video element</p>
          <video controls class="mt-4 w-full max-w-md">
            <source :src="src" />
          </video>
        </div>
      </div>
    `,
    props: ['src']
  },
  delay: 200,
  timeout: 10000
})

// Nuxt 3 composables for SEO
useSeoMeta({
  title: 'Video Player - Universal Player Demo',
  description: 'Universal Video Player with enhanced features including watermark protection, quality selector, and picture-in-picture support'
})

// Alternative: Use useHead for more control
useHead({
  title: 'Video Player - Universal Player Demo',
  meta: [
    { name: 'description', content: 'Universal Video Player with enhanced features including watermark protection, quality selector, and picture-in-picture support' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { property: 'og:title', content: 'Video Player - Universal Player Demo' },
    { property: 'og:description', content: 'Universal Video Player with enhanced features' }
  ]
})

// Player Reference
const playerRef = ref(null)

// Player State
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
const progress = computed(() => duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0)
const currentQuality = ref('')
const playerStatus = computed(() => {
  if (isPlaying.value) return 'Playing'
  if (currentTime.value > 0) return 'Paused'
  return 'Stopped'
})

// ===== PLAYER CONFIGURATION (จัดกลุ่มตาม domain) =====

// 1. Video Basic Configuration
const videoConfig = reactive({
  src: contentDeliveryClient.getStreamUrl({ id: primaryStreamId, quality: 'auto' }),
  poster: '', // ใช้ autoCover แทน thumbnail API ที่ไม่มีอยู่
  height: 500,
  autoplay: false,
  muted: false,
  loop: false,
  preload: 'metadata',
  objectFit: 'contain',
  coverObjectFit: 'cover', // ลองเปลี่ยนเป็น 'contain' ถ้าต้องการแสดงครบทั้งรูป
  autoCover: true
})

// 2. Player UI Controls Configuration  
const playerControls = reactive({
  showBigPlayButton: true,
  showQualitySelector: true,
  showFullscreenButton: true,
  showPipButton: true,
  showSkipButtons: true
})

// 3. Styling & Theme Configuration
const styling = reactive({
  themeColor: '#3b82f6' // Blue-500
})

// 4. Overlay Configuration
const overlayConfig = reactive({
  show: true,
  label: 'Content Delivery Stream',
  subtitle: 'Secure Video Streaming'
})

// 5. Logo Configuration
const logoConfig = reactive({
  show: true,
  url: 'https://doa-academy.sgp1.digitaloceanspaces.com/2024/02/1707689052066.png',
  alt: 'DOA Academy Logo',
  width: '200px',
  maxWidth: '150px',
  position: 'top-right',
  alwaysShow: true
})

// 6. Watermark Security Configuration
const watermarkConfig = reactive({
  show: true, // เปิดใช้งาน watermark สำหรับป้องกันเนื้อหา
  title: 'MANONSANOI.COM',
  text: 'PROTECTED CONTENT',
  subtitle: 'Unauthorized copying prohibited',
  opacity: '0.12',
  color: '#ffffff'
})

// 7. Player Security Configuration
const securityConfig = reactive({
  disableSeeker: false // ปิดการใช้งาน seeker bar และ skip buttons
})

// 8. Preview Mode Configuration
const previewConfig = reactive({
  enabled: false,
  startTime: 30, // วินาที
  endTime: 90,   // วินาที
  loop: true,
  startTimeInput: '00:30', // input แสดงผล
  endTimeInput: '01:30'    // input แสดงผล
})

// 9. Controls Behavior Configuration
const controlsConfig = reactive({
  autoHide: true,           // เปิด/ปิด การซ่อนอัตโนมัติ
  alwaysShow: false,        // แสดงตลอดเวลา
  hideDelay: 3000,          // เวลาในการซ่อน (ms)
  showOnHover: true,        // แสดงเมื่อ hover (desktop)
  showOnTouch: true         // แสดงเมื่อแตะ (mobile)
})

// 10. Subtitle Configuration
const subtitleConfig = reactive({
  enabled: true,
  currentTrack: 'th',
  tracks: [
    {
      id: 'th',
      label: 'Thai (ไทย)',
      url: '/subtitles/thai.vtt',
      language: 'th',
      default: true
    },
    {
      id: 'en',
      label: 'English',
      url: '/subtitles/english.vtt',
      language: 'en',
      default: false
    },
    {
      id: 'zh',
      label: '中文 (Chinese)',
      url: '/subtitles/chinese.vtt',
      language: 'zh',
      default: false
    },
    {
      id: 'off',
      label: 'ปิดคำบรรยาย',
      url: '',
      language: '',
      default: false
    }
  ]
})

// 11. Soundtrack Configuration
const soundtrackConfig = reactive({
  enabled: true,
  currentTrack: 'original',
  tracks: [
    {
      id: 'original',
      label: 'เสียงต้นฉบับ',
      url: '',
      language: 'th',
      default: true
    },
    {
      id: 'english',
      label: 'English Audio',
      url: '/audio/english.mp3',
      language: 'en',
      default: false
    },
    {
      id: 'japanese',
      label: '日本語 (Japanese)',
      url: '/audio/japanese.mp3',
      language: 'ja',
      default: false
    },
    {
      id: 'chinese',
      label: '中文 (Chinese)',
      url: '/audio/chinese.mp3',
      language: 'zh',
      default: false
    }
  ]
})

// ฟังก์ชันแปลงเวลาจาก MM:SS เป็นวินาที
const parseTimeInput = (input) => {
  if (!input) return 0
  
  const str = input.toString().trim()
  
  // ถ้าเป็นรูปแบบ MM:SS หรือ M:SS
  if (str.includes(':')) {
    const parts = str.split(':')
    if (parts.length === 2) {
      const minutes = parseInt(parts[0]) || 0
      const seconds = parseInt(parts[1]) || 0
      return minutes * 60 + seconds
    }
  }
  
  // ถ้าเป็นตัวเลขธรรมดา (วินาที)
  return parseInt(str) || 0
}

// ฟังก์ชันแปลงจากวินาทีเป็น MM:SS
const formatTimeToDisplay = (seconds) => {
  if (!seconds && seconds !== 0) return '00:00'
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

// Watch สำหรับแปลงเวลาอัตโนมัติ
watch(() => previewConfig.startTimeInput, (newVal) => {
  previewConfig.startTime = parseTimeInput(newVal)
})

watch(() => previewConfig.endTimeInput, (newVal) => {
  previewConfig.endTime = parseTimeInput(newVal)
})

// Thumbnail fit mode controls (สำหรับทดสอบ)
const availableFitModes = [
  { value: 'cover', label: 'Cover (เต็มพื้นที่)', desc: 'เหมาะสำหรับ thumbnail' },
  { value: 'contain', label: 'Contain (พอดีทั้งหมด)', desc: 'แสดงครบไม่ตัดขอบ' },
  { value: 'fill', label: 'Fill (ยืดเต็ม)', desc: 'อาจเพี้ยนรูป' },
  { value: 'scale-down', label: 'Scale Down (ย่อลง)', desc: 'ย่อให้พอดี' },
  { value: 'none', label: 'None (ขนาดจริง)', desc: 'ไม่ปรับขนาด' }
]

// Primary stream ID
const primaryStreamId = '64520ed4587620552cd1c093'

// ===== COMPOSABLE FUNCTIONS FOR GROUPED CONFIGURATION =====

// Helper function for easy configuration updates
const updateVideoConfig = (updates) => {
  Object.assign(videoConfig, updates)
}

const updatePlayerControls = (updates) => {
  Object.assign(playerControls, updates)
}

const updateWatermarkConfig = (updates) => {
  Object.assign(watermarkConfig, updates)
}

const updateOverlayConfig = (updates) => {
  Object.assign(overlayConfig, updates)
}

const updateLogoConfig = (updates) => {
  Object.assign(logoConfig, updates)
}

// Combined Player Configuration (จัดกลุ่ม props ให้เป็น object เดียว)
const playerConfig = computed(() => ({
  // Video Configuration (ใช้ spread operator)
  ...videoConfig,
  
  // Player Controls (เพิ่ม prefix เพื่อไม่ให้ซ้ำกัน)
  showBigPlayButton: playerControls.showBigPlayButton,
  showQualitySelector: playerControls.showQualitySelector,
  showFullscreenButton: playerControls.showFullscreenButton,
  showPipButton: playerControls.showPipButton,
  showSkipButtons: playerControls.showSkipButtons,
  
  // Security Configuration
  disableSeeker: securityConfig.disableSeeker,
  streamSecure: true, // เปิดใช้งาน secure stream token สำหรับ API ใหม่
  
  // Preview Mode Configuration
  previewMode: previewConfig.enabled,
  previewStart: previewConfig.startTime,
  previewEnd: previewConfig.endTime,
  previewLoop: previewConfig.loop,
  
  // Styling
  themeColor: styling.themeColor,
  
  // Overlay Configuration (เพิ่ม prefix เพื่อไม่ให้ซ้ำกับ show ใน playerControls)
  overlayLabel: overlayConfig.label,
  overlaySubtitle: overlayConfig.subtitle,
  showOverlayLabel: overlayConfig.show,
  
  // Logo Configuration (ส่งเป็น object)
  logo: logoConfig,
  
  // Watermark Configuration (เพิ่ม prefix)
  showWatermark: watermarkConfig.show,
  watermarkTitle: watermarkConfig.title,
  watermarkText: watermarkConfig.text,
  watermarkSubtitle: watermarkConfig.subtitle,
  watermarkOpacity: watermarkConfig.opacity,
  watermarkColor: watermarkConfig.color,
  
  // Subtitle Configuration
  subtitleEnabled: subtitleConfig.enabled,
  subtitleTracks: subtitleConfig.tracks,
  currentSubtitle: subtitleConfig.currentTrack,
  
  // Soundtrack Configuration  
  soundtrackEnabled: soundtrackConfig.enabled,
  soundtrackTracks: soundtrackConfig.tracks,
  currentSoundtrack: soundtrackConfig.currentTrack
}))

// Demo functions for easy testing
const presetConfigurations = {
  // ตัวอย่างการตั้งค่าต่างๆ แบบง่าย
  enableAllFeatures: () => {
    updatePlayerControls({
      showBigPlayButton: true,
      showQualitySelector: true,
      showFullscreenButton: true,
      showPipButton: true,
      showSkipButtons: true
    })
    updateWatermarkConfig({ show: true })
    updateOverlayConfig({ show: true })
    // Control settings for full experience
    controlsConfig.autoHide = true
    controlsConfig.alwaysShow = false
    controlsConfig.hideDelay = 3000
    controlsConfig.showOnHover = true
    controlsConfig.showOnTouch = true
  },
  
  minimalPlayer: () => {
    updatePlayerControls({
      showBigPlayButton: true,
      showQualitySelector: false,
      showFullscreenButton: false,
      showPipButton: false,
      showSkipButtons: false
    })
    updateWatermarkConfig({ show: false })
    updateOverlayConfig({ show: false })
    // Always show controls for simplicity
    controlsConfig.autoHide = false
    controlsConfig.alwaysShow = true
  },
  
  securityMode: () => {
    updateWatermarkConfig({
      show: true,
      title: 'CONFIDENTIAL CONTENT',
      text: 'PROTECTED MATERIAL',
      subtitle: 'Viewing monitored and recorded'
    })
    // Hide controls automatically for security
    controlsConfig.autoHide = true
    controlsConfig.alwaysShow = false
    controlsConfig.hideDelay = 2000
    controlsConfig.showOnHover = true
    controlsConfig.showOnTouch = true
  },
  
  // Preset สำหรับผู้ใช้หลากภาษา
  multiLanguageMode: () => {
    // เปิดใช้งาน subtitle และ soundtrack
    subtitleConfig.enabled = true
    subtitleConfig.currentTrack = 'th' // เริ่มต้นด้วยภาษาไทย
    
    soundtrackConfig.enabled = true
    soundtrackConfig.currentTrack = 'original'
    
    // เปิดฟีเจอร์ที่จำเป็นสำหรับหลากภาษา
    updatePlayerControls({
      showBigPlayButton: true,
      showQualitySelector: true,
      showFullscreenButton: true,
      showPipButton: true,
      showSkipButtons: true
    })
    
    console.log('🌍 Multi-Language mode activated')
  },
  
  // Preset สำหรับการเน้นเสียง (เช่น podcast หรือการเรียน)
  audioOnlyMode: () => {
    // เปิดใช้งาน soundtrack แต่ปิด subtitle
    subtitleConfig.enabled = false
    subtitleConfig.currentTrack = 'off'
    
    soundtrackConfig.enabled = true
    soundtrackConfig.currentTrack = 'original'
    
    // ปิดฟีเจอร์ที่ไม่จำเป็นสำหรับการฟัง
    updatePlayerControls({
      showBigPlayButton: true,
      showQualitySelector: false,
      showFullscreenButton: false,
      showPipButton: false,
      showSkipButtons: true
    })
    
    // ปิด overlay และ watermark เพื่อลดการรบกวน
    updateWatermarkConfig({ show: false })
    updateOverlayConfig({ show: false })
    
    console.log('🎵 Audio Focus mode activated')
  }
}

// Video Sources with multiple quality options
const videoSources = reactive([
  // Multi-quality streams from Content Delivery API
  ...getMultiQualityStreams(primaryStreamId, 'Content Stream'),
  
  // Separator for YouTube content
  { name: '--- YouTube Sources ---', url: '', poster: '', disabled: true },
  
  // YouTube Direct URLs (will be processed through YouTube API)
  {
    name: 'YouTube - Test Video',
    url: 'https://www.youtube.com/watch?v=aLQGkXD3HG8&ab_channel=12KVIDEOULTRAHD',
    youtubeId: 'aLQGkXD3HG8',
    isYoutube: true,
    poster: ''
  },
  
  // HLS/M3U8 sources (similar to YouTube adaptive streaming)
  {
    name: 'M3U8 Stream - Cloudflare',
    url: 'https://customer-apw77h9sea196rll.cloudflarestream.com/e9f27465089b45a2a08bc4a07d4229d9/manifest/video.m3u8',
    poster: ''
  },
  
  // Separator for demo content
  { name: '--- Demo Content ---', url: '', poster: '', disabled: true },
  
  // MP4 Video Source
  {
    name: 'MP4 Video - Digital Ocean',
    url: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/09/1757386142473.mp4',
    poster: ''
  }
])

// Import API Configuration
import { API_CONFIG } from '~/config/api-endpoints.js'

// API Configuration for Content Delivery (ใช้จาก API_CONFIG)
const apiConfig = reactive({
  baseUrl: API_CONFIG.CONTENT_DELIVERY.baseUrl,
  headers: {
    'Content-Type': 'application/json',
    // Add authentication headers if needed
    // 'Authorization': 'Bearer your-token-here'
  }
})

// Player Event Handlers
const onPlayerReady = () => {
  console.log('Player is ready')
  console.log('Loading video from Content Delivery API:', videoConfig.src)
}

const onPlayerPlay = () => {
  isPlaying.value = true
  console.log('Video started playing')
}

const onPlayerPause = () => {
  isPlaying.value = false
  console.log('Video paused')
}

const onTimeUpdate = (time) => {
  currentTime.value = time.currentTime
  duration.value = time.duration
}

const onPlayerError = (error) => {
  console.error('Player error:', error)
  
  // Handle specific errors from Content Delivery API
  if (error.code === 'NETWORK_ERROR' || error.message?.includes('Failed to fetch')) {
    console.error('Network error - check Content Delivery API status')
    // You could show a user-friendly error message
  } else if (error.code === 'MEDIA_ERR_SRC_NOT_SUPPORTED') {
    console.error('Video format not supported or stream unavailable')
  }
  
  // You could show a toast notification or error message here
  // For example: 
  // showErrorToast('วิดีโอไม่สามารถโหลดได้ กรุณาลองใหม่อีกครั้ง')
}

const onQualityChanged = (quality) => {
  currentQuality.value = quality
  console.log('Quality changed to:', quality)
}

const onEnterPip = () => {
  console.log('Entered Picture-in-Picture mode')
}

const onLeavePip = () => {
  console.log('Left Picture-in-Picture mode')
}

// Subtitle & Soundtrack Event Handlers (รับจาก UniversalPlayer)
const onSubtitleTrackChanged = (trackInfo) => {
  console.log('Subtitle track changed from UniversalPlayer:', trackInfo)
  
  // Update local state to sync with player
  if (trackInfo.id) {
    subtitleConfig.currentTrack = trackInfo.id
    subtitleConfig.enabled = trackInfo.enabled
  }
}

const onSoundtrackChanged = (trackInfo) => {
  console.log('Soundtrack changed from UniversalPlayer:', trackInfo)
  
  // Update local state to sync with player
  if (trackInfo.id) {
    soundtrackConfig.currentTrack = trackInfo.id
    soundtrackConfig.enabled = trackInfo.enabled
  }
}

const onSubtitleError = (error) => {
  console.error('Subtitle error from UniversalPlayer:', error)
  
  // Handle subtitle loading errors
  if (error.type === 'SUBTITLE_LOAD_ERROR') {
    console.warn('Failed to load subtitle:', error.url)
    // อาจแสดง toast notification หรือเปลี่ยนกลับเป็น subtitle อื่น
  }
}

const onSoundtrackError = (error) => {
  console.error('Soundtrack error from UniversalPlayer:', error)
  
  // Handle soundtrack loading errors  
  if (error.type === 'SOUNDTRACK_LOAD_ERROR') {
    console.warn('Failed to load soundtrack:', error.url)
    // กลับไปใช้เสียงต้นฉบับ
    soundtrackConfig.currentTrack = 'original'
  }
}

// Player Control Methods
const togglePlay = () => {
  if (playerRef.value) {
    playerRef.value.togglePlay()
  } else {
    console.warn('[Parent] playerRef.value is null, cannot toggle play')
  }
}

const stopVideo = () => {
  if (playerRef.value) {
    playerRef.value.stop()
    isPlaying.value = false
    currentTime.value = 0
  } else {
    console.warn('[Parent] playerRef.value is null, cannot stop video')
  }
}

const skipBack = () => {
  if (playerRef.value) {
    if (typeof playerRef.value.skipBackward === 'function') {
      playerRef.value.skipBackward()
    } else {
      console.error('[Parent] skipBackward method not found on player')
    }
  } else {
    console.warn('[Parent] playerRef.value is null, cannot skip back')
  }
}

const skipForward = () => {
  if (playerRef.value) {
    if (typeof playerRef.value.skipForward === 'function') {
      playerRef.value.skipForward()
    } else {
      console.error('[Parent] skipForward method not found on player')
    }
  } else {
    console.warn('[Parent] playerRef.value is null, cannot skip forward')
  }
}

const toggleFullscreen = () => {
  if (playerRef.value) {
    if (typeof playerRef.value.toggleFullscreen === 'function') {
      playerRef.value.toggleFullscreen()
    } else {
      console.error('[Parent] toggleFullscreen method not found on player')
    }
  } else {
    console.warn('[Parent] playerRef.value is null, cannot toggle fullscreen')
  }
}

const togglePictureInPicture = () => {
  if (playerRef.value) {
    if (typeof playerRef.value.togglePictureInPicture === 'function') {
      playerRef.value.togglePictureInPicture()
    } else {
      console.error('[Parent] togglePictureInPicture method not found on player')
    }
  } else {
    console.warn('[Parent] playerRef.value is null, cannot toggle PiP')
  }
}

const toggleWatermark = () => {
  watermarkConfig.show = !watermarkConfig.show
}

const toggleSeeker = () => {
  securityConfig.disableSeeker = !securityConfig.disableSeeker
}

const togglePreviewMode = () => {
  previewConfig.enabled = !previewConfig.enabled
}

const toggleLogo = () => {
  logoConfig.show = !logoConfig.show
}

// Subtitle & Soundtrack Functions
const toggleSubtitles = () => {
  subtitleConfig.enabled = !subtitleConfig.enabled
  console.log('Subtitle toggled:', subtitleConfig.enabled)
  
  // ถ้าปิด subtitle ให้เลือกเป็น 'off'
  if (!subtitleConfig.enabled) {
    subtitleConfig.currentTrack = 'off'
  } else {
    // ถ้าเปิด subtitle ให้เลือก default track
    const defaultTrack = subtitleConfig.tracks.find(track => track.default && track.id !== 'off')
    if (defaultTrack) {
      subtitleConfig.currentTrack = defaultTrack.id
    }
  }
}

const toggleSoundtrack = () => {
  soundtrackConfig.enabled = !soundtrackConfig.enabled
  console.log('Soundtrack toggled:', soundtrackConfig.enabled)
  
  // ถ้าปิด soundtrack ให้กลับเป็น original
  if (!soundtrackConfig.enabled) {
    soundtrackConfig.currentTrack = 'original'
  }
}

const onSubtitleChange = () => {
  console.log('Subtitle changed to:', subtitleConfig.currentTrack)
  
  const selectedTrack = subtitleConfig.tracks.find(track => track.id === subtitleConfig.currentTrack)
  
  if (selectedTrack) {
    if (selectedTrack.id === 'off') {
      subtitleConfig.enabled = false
    } else {
      subtitleConfig.enabled = true
    }
    
    // ส่งเหตุการณ์ไปยัง UniversalPlayer
    if (playerRef.value && typeof playerRef.value.updateSubtitle === 'function') {
      playerRef.value.updateSubtitle({
        id: selectedTrack.id,
        url: selectedTrack.url,
        language: selectedTrack.language,
        enabled: subtitleConfig.enabled
      })
    }
  }
}

const onSoundtrackChange = () => {
  console.log('Soundtrack changed to:', soundtrackConfig.currentTrack)
  
  const selectedTrack = soundtrackConfig.tracks.find(track => track.id === soundtrackConfig.currentTrack)
  
  if (selectedTrack) {
    // ส่งเหตุการณ์ไปยัง UniversalPlayer
    if (playerRef.value && typeof playerRef.value.updateSoundtrack === 'function') {
      playerRef.value.updateSoundtrack({
        id: selectedTrack.id,
        url: selectedTrack.url,
        language: selectedTrack.language,
        enabled: soundtrackConfig.enabled
      })
    }
  }
}

// Helper functions for current labels
const getCurrentSubtitleLabel = () => {
  const track = subtitleConfig.tracks.find(t => t.id === subtitleConfig.currentTrack)
  return track ? track.label : 'ไม่พบ'
}

const getCurrentSoundtrackLabel = () => {
  const track = soundtrackConfig.tracks.find(t => t.id === soundtrackConfig.currentTrack)
  return track ? track.label : 'ไม่พบ'
}

// Control Bar Settings Functions
const toggleAutoHide = () => {
  controlsConfig.autoHide = !controlsConfig.autoHide
  // ถ้าเปิด auto hide ก็ปิด always show
  if (controlsConfig.autoHide) {
    controlsConfig.alwaysShow = false
  }
}

const toggleAlwaysShow = () => {
  controlsConfig.alwaysShow = !controlsConfig.alwaysShow
  // ถ้าเปิด always show ก็ปิด auto hide
  if (controlsConfig.alwaysShow) {
    controlsConfig.autoHide = false
  }
}

const cycleHideDelay = () => {
  // วนค่า delay: 1s -> 2s -> 3s -> 5s -> 10s -> 1s
  const delays = [1000, 2000, 3000, 5000, 10000]
  const currentIndex = delays.indexOf(controlsConfig.hideDelay)
  const nextIndex = (currentIndex + 1) % delays.length
  controlsConfig.hideDelay = delays[nextIndex]
}

const toggleHoverShow = () => {
  controlsConfig.showOnHover = !controlsConfig.showOnHover
}

const changeVideoSource = async (source) => {
  try {
    // Show loading state
    console.log('Switching to video source:', source.name)
    
    // Convert YouTube URL to embed format if needed
    let finalUrl = source.url
    if (source.isYoutube || source.url.includes('youtube.com/watch') || source.url.includes('youtu.be/')) {
      const videoId = source.youtubeId || extractYouTubeId(source.url)
      if (videoId) {
        finalUrl = `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=0`
        console.log('🎬 [YOUTUBE] Converted to embed URL:', finalUrl)
      }
    }
    
    videoConfig.src = finalUrl
    videoConfig.poster = source.poster
    overlayConfig.label = source.name
    
    // Update video source
    if (playerRef.value) {
      await playerRef.value.changeSource(finalUrl)
    }
    
    console.log('Video source changed successfully')
  } catch (error) {
    console.error('Failed to change video source:', error)
    onPlayerError({
      code: 'SOURCE_CHANGE_ERROR',
      message: 'Failed to load new video source',
      originalError: error
    })
  }
}

// Helper function to extract YouTube video ID
const extractYouTubeId = (url) => {
  if (!url) return null
  
  let videoId = ''
  
  if (url.includes('youtu.be/')) {
    // Format: https://youtu.be/VIDEO_ID
    videoId = url.split('youtu.be/')[1].split('?')[0]
  } else if (url.includes('youtube.com/watch')) {
    // Format: https://www.youtube.com/watch?v=VIDEO_ID
    const urlParams = new URLSearchParams(url.split('?')[1])
    videoId = urlParams.get('v')
  } else if (url.includes('youtube.com/embed/')) {
    // Format: https://www.youtube.com/embed/VIDEO_ID
    videoId = url.split('embed/')[1].split('?')[0]
  }
  
  return videoId || null
}

// Quick source switcher functions
const switchToYoutubeMp4 = () => {
  // Find YouTube source
  const youtubeSource = videoSources.find(source => 
    source.isYoutube && !source.disabled
  )
  
  if (youtubeSource) {
    changeVideoSource(youtubeSource)
  }
}

const switchToM3u8 = () => {
  // Find M3U8 source (Cloudflare stream)
  const m3u8Source = videoSources.find(source => 
    source.url.includes('.m3u8') && !source.disabled
  )
  
  if (m3u8Source) {
    changeVideoSource(m3u8Source)
  } else {
    console.warn('No M3U8 source found')
  }
}

const switchToMp4 = () => {
  // Find MP4 source (Digital Ocean)
  const mp4Source = videoSources.find(source => 
    source.url.includes('.mp4') && !source.disabled && !source.isYoutube
  )
  
  if (mp4Source) {
    changeVideoSource(mp4Source)
  } else {
    console.warn('No MP4 source found')
  }
}

const switchToOriginal = () => {
  // Switch back to the first non-disabled source (usually the primary stream)
  const originalSource = videoSources.find(source => !source.disabled)
  if (originalSource) {
    changeVideoSource(originalSource)
  }
}

// Helper functions
const getSourceType = (url) => {
  if (!url) return 'None'
  if (url.includes('.m3u8')) return 'HLS/M3U8 Stream'
  if (url.includes('.mp4')) return 'MP4 Video'
  if (url.includes('.webm')) return 'WebM Video'
  if (url.includes('youtube')) return 'YouTube Source'
  if (url.includes('stream')) return 'Live Stream'
  return 'Unknown Format'
}

// Helper function to check API status
const checkApiStatus = async () => {
  try {
    const response = await fetch(`${apiConfig.baseUrl}/health`, {
      method: 'HEAD',
      headers: apiConfig.headers
    })
    return response.ok
  } catch (error) {
    console.warn('API health check failed:', error)
    return false
  }
}

// Player Status Helper Functions - Made reactive
const getPlayerStatus = () => {
  if (!playerRef.value) return 'Loading'
  try {
    // Force reactivity by checking isPlaying state
    if (isPlaying.value) return 'Playing'
    if (currentTime.value > 0) return 'Paused'
    return 'Stopped'
  } catch (error) {
    console.warn('[Player Status] Error getting player status:', error)
    return 'Unknown'
  }
}

const getPlayerCurrentTime = () => {
  // Use reactive currentTime value from onTimeUpdate
  return currentTime.value || 0
}

const getPlayerDuration = () => {
  // Use reactive duration value from onTimeUpdate
  return duration.value || 0
}

const getPlayerProgress = () => {
  const duration = getPlayerDuration()
  const currentTime = getPlayerCurrentTime()
  
  if (duration > 0) {
    return Math.round((currentTime / duration) * 100)
  }
  return 0
}

const getPlayerQuality = () => {
  if (!playerRef.value) return 'Auto'
  try {
    return playerRef.value.currentQualityLabel || playerRef.value.selectedQuality || 'Auto'
  } catch (error) {
    console.warn('[Player Status] Error getting quality:', error)
    return 'Auto'
  }
}

const getSessionId = () => {
  if (!playerRef.value) {
    return null
  }
  
  if (!playerRef.value.currentSessionId) return null
  
  try {
    const sessionValue = playerRef.value.currentSessionId
    // Handle both ref and direct values
    return (sessionValue && sessionValue.value) ? sessionValue.value : sessionValue
  } catch (error) {
    console.warn('[Player Status] Error getting session ID:', error)
    return null
  }
}

// Utility Functions
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '00:00'
  
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)
  
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

// Component Lifecycle
onMounted(async () => {
  // Set client-side flag
  isClient.value = true
  //console.log('[Parent] Player component mounted on client-side')
  
  // Wait for component to be ready
  await nextTick()
  
  // Check if UniversalPlayer is ready
  let retryCount = 0
  const maxRetries = 10
  
  const waitForPlayerRef = () => {
    return new Promise((resolve) => {
      const checkPlayer = () => {
        retryCount++
        
        if (playerRef.value) {
          resolve(true)
        } else if (retryCount < maxRetries) {
          setTimeout(checkPlayer, 100)
        } else {
          console.warn('[Parent] PlayerRef not ready after max retries')
          resolve(false)
        }
      }
      checkPlayer()
    })
  }
  
  await waitForPlayerRef()
  
  // Check API status
  const apiIsHealthy = await checkApiStatus()
  if (!apiIsHealthy) {
    console.warn('Content Delivery API may be unavailable')
  }
  
  // Initialize any client-side only features here
  // For example, keyboard shortcuts, fullscreen API, etc.
  
  // Add keyboard shortcuts for video control
  const handleKeyPress = (event) => {
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return // Don't interfere with form inputs
    }
    
    switch (event.code) {
      case 'Space':
        event.preventDefault()
        togglePlay()
        break
      case 'ArrowLeft':
        event.preventDefault()
        skipBack()
        break
      case 'ArrowRight':
        event.preventDefault()
        skipForward()
        break
      case 'KeyF':
        event.preventDefault()
        toggleFullscreen()
        break
      case 'KeyM':
        event.preventDefault()
        if (playerRef.value) {
          playerRef.value.toggleMute()
        }
        break
    }
  }
  
  document.addEventListener('keydown', handleKeyPress)
  
  // Store reference for cleanup
  keyboardHandler.value = handleKeyPress
})

// Store keyboard handler reference for cleanup
const keyboardHandler = ref(null)

// Cleanup on unmount
onUnmounted(() => {
  if (keyboardHandler.value) {
    document.removeEventListener('keydown', keyboardHandler.value)
  }
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
